import { getUserAccount } from '../user/selectors';
import { getTotalAvailability } from '../util/mapAvailability';

import {
  ORDER_TYPE_ATONCE,
  ORDER_TYPE_FORECAST,
  ORDER_TYPE_FUTURES,
  PAYMENT_BILLABLE,
  PAYMENT_COMP,
  PAYMENT_UNIT,
  PAYMENT_COMP_CASH,
  PAYMENT_COMP_UNIT,
  paymentMethodsSelect
} from '../shop/constants';

import {
  ERROR_TYPE_CART,
  ERROR_TYPE_CHECKOUT,
  ERROR_TYPES_ALL,
  ERROR_DATA_PHONENUM,
  ERROR_DATA_PHONENUM_EXT,
  ERROR_DATA_PURCHASE_ORDER_NUM,
  ERROR_DATA_SHIPPING_METHOD,
  ERROR_DATA_NOTES_TO_NIKE,
  errorIsOfType
} from './util/errors';
import { isEqual } from '../util/lodash';

import { findProductInfo, DEFAULT_PRODUCT_INFO_STRUCTURE } from '../productsInfo';

export const ADD_TO_CART_ERROR_MESSAGE = 'ADD_TO_CART_ERROR';

export const DEFAULT_CURRENCY_CODE = 'USD';

export const SHIPPING_METHOD_STANDARD = 'Standard';
export const SHIPPING_METHOD_OVERNIGHT = 'Overnight';
export const SHIPPING_METHOD_2ND_DAY = '2-day';
export const SHIPPING_METHOD_3_DAY = '3-day';
export const SHIPPING_METHOD_EXPEDITED = 'Expedited';

export const shippingMethodsMap = new Map([
  [SHIPPING_METHOD_STANDARD, 'STANDARD'],
  [SHIPPING_METHOD_OVERNIGHT, 'OVERNIGHT'],
  [SHIPPING_METHOD_2ND_DAY, '2ND_DAY'],
  [SHIPPING_METHOD_3_DAY, '3_DAY'],
  [SHIPPING_METHOD_EXPEDITED, '2ND_DAY'] // BE will use 2ND_DAY for 'expedited' shipping
]);

export const CART_TYPES = {
  AT_ONCE: 'Z011',
  FUTURES: 'Z008',
  FORECAST: 'Z010'
};

export const orderTypeToCartType = orderType => {
  switch (orderType) {
    case ORDER_TYPE_ATONCE:
      return CART_TYPES.AT_ONCE;
    case ORDER_TYPE_FUTURES:
      return CART_TYPES.FUTURES;
    case ORDER_TYPE_FORECAST:
      return CART_TYPES.FORECAST;
    default:
      return '';
  }
};

export const cartTypeToOrderType = cartType => {
  switch (cartType) {
    case CART_TYPES.AT_ONCE:
      return ORDER_TYPE_ATONCE;
    case CART_TYPES.FUTURES:
      return ORDER_TYPE_FUTURES;
    case CART_TYPES.FORECAST:
      return ORDER_TYPE_FORECAST;
    default:
      return '';
  }
};

export const paymentTypeToCartProductPaymentType = paymentType => {
  switch (paymentType) {
    case PAYMENT_COMP:
      return PAYMENT_COMP_CASH;
    case PAYMENT_UNIT:
      return PAYMENT_COMP_UNIT;
    default:
      return paymentType;
  }
};

const PRODUCT_ATTRIBUTE_SILHOUETTE = 'SIL';
const PRODUCT_ATTRIBUTE_DIVISION = 'DIV';
const FOOTWEAR_ATTRIBUTE_VALUE = '20';
const FOOTWEAR_DIVISION_VALUE = 'FTWR';
const APPAREL_ATTRIBUTE_VALUE = '10';
const APPAREL_DIVISION_VALUE = 'APRL';
const EQUIPMENT_ATTRIBUTE_VALUE = '30';
const EQUIPMENT_DIVISION_VALUE = 'EQMT';

export const isCartTypeCode = code => code.startsWith('Z');

export const STATUS = {
  PENDING: 'PENDING_APPROVAL',
  SAVED: 'OPEN',
  SUBMITTED: 'SUBMITTED'
};

export const CART_QUANTITY_MAXLEN = 4;

// Get all carts for the provided account number
export const getCarts = (state, accountNumber) => state?.carts?.accounts?.[accountNumber] || [];

// Get the "server cart" of the same ID as the currently selected cart. This allows us to see the 'server truth' vs
// the 'user truth' as represented by current cart
export const getServerCart = (state, cartId) => {
  const account = getUserAccount(state);
  const accountCarts = (account && getCarts(state, account.number)) || [];

  return accountCarts.find(cart => cart.id === cartId);
};

// Returns the current cart. (aka 'user truth')
export const getCurrentCart = state => state.carts.currentCart;

export const getCurrentCartTotal = state => {
  const currentCart = getCurrentCart(state);
  return currentCart.totals.comp + currentCart.totals.billable;
};

export const getCurrentCartRetailTotal = state => {
  const currentCart = getCurrentCart(state);
  return currentCart.totals.retail;
};

// Returns the product count of the server truth
export const getServerCartProductCount = state => {
  const serverCart = getServerCart(state, getCurrentCart(state).id);
  return serverCart ? serverCart.products.length : 0;
};

// Returns the product IDs array of the current cart
export const getProductIds = state => getCurrentCart(state).sortedAndFilteredProductIds || [];

// Returns a cart item in the current cart by it's ID
export const getCartProductById = (state, cartProductId) =>
  getCurrentCart(state).products.find(product => product.id === cartProductId);

// Returns a boolean of whether the cart has fundamental product
export const currentCartHasFundamentalItems = state =>
  getCurrentCart(state).products.some(item => !!item.fundamentalRuleId);

// Returns a cart attribute by it's attribute name (or undefined if not present)
export const getCartAttribute = (state, attributeName) => getCurrentCart(state)[attributeName];

// Digs into the shop reducer to return availability data based on the provided productCode
// TODO: Move this into the shop reducer
export const getAvailabilityForProductCode = (state, productCode) => {
  const availability = state.shop.productAvailability;

  return availability[productCode];
};

// Returns the count of the current cart item list
export const cartCount = state => getCurrentCart(state).products.length;

// Return whether the cart is loading based on {requests} provided
// TODO: Probably re-name this to something more appropriate..
export const cartIsLoading = (state, requests) =>
  requests.some(requestName => {
    const request = state.request[requestName];

    return (request && request.isPending) || false;
  });

export const getCartProductsFetched = state => getCurrentCart(state).hasFetchedProducts;

export const filterUnitAllotmentsForProduct = (productInfo = DEFAULT_PRODUCT_INFO_STRUCTURE, unitAllotments = []) =>
  unitAllotments.filter(allotment => {
    switch (allotment.unitAttributeName) {
      case PRODUCT_ATTRIBUTE_DIVISION:
        return (
          (allotment.unitAttributeValue === FOOTWEAR_ATTRIBUTE_VALUE &&
            productInfo.division === FOOTWEAR_DIVISION_VALUE) ||
          (allotment.unitAttributeValue === APPAREL_ATTRIBUTE_VALUE &&
            productInfo.division === APPAREL_DIVISION_VALUE) ||
          (allotment.unitAttributeValue === EQUIPMENT_ATTRIBUTE_VALUE &&
            productInfo.division === EQUIPMENT_DIVISION_VALUE)
        );
      case PRODUCT_ATTRIBUTE_SILHOUETTE:
        return allotment.unitAttributeValue === productInfo.silhouette.code;
      default:
        return allotment.unitAttributeValue === productInfo.silhouetteType.code;
    }
  });

// Get available payment methods for the given cart item
export const getPaymentMethods = (selectedDepartment, useCurrentContract, accountIsBillable) => {
  const departmentType = selectedDepartment.type;
  const methods = [];
  const comp = paymentMethodsSelect.find(method => method.value === PAYMENT_COMP);
  const billable = paymentMethodsSelect.find(method => method.value === PAYMENT_BILLABLE);

  if (departmentType === ORDER_TYPE_ATONCE || useCurrentContract) {
    methods.push(comp);
    if (accountIsBillable && selectedDepartment.currentBillable) methods.push(billable);
  } else if (departmentType === ORDER_TYPE_FUTURES) {
    if (!useCurrentContract) methods.push(comp);
    if (accountIsBillable && !useCurrentContract && selectedDepartment.futureBillable) methods.push(billable);
  }

  return methods;
};

export const getPaymentMethodsForCartProduct = (
  productInfo,
  selectedDepartment,
  useCurrentContract,
  accountIsBillable,
  unitAllotments
) => {
  const methods = getPaymentMethods(selectedDepartment, useCurrentContract, accountIsBillable);
  const unit = paymentMethodsSelect.find(method => method.value === PAYMENT_UNIT);
  const itemUnitAllotments = filterUnitAllotmentsForProduct(productInfo, unitAllotments);

  if (itemUnitAllotments.length > 0) methods.push(unit);

  return methods;
};

export const cartHasMultiplePaymentTypes = state =>
  [...Set(getCurrentCart(state).products.map(cartProduct => cartProduct.paymentMethod))].length > 1;

export const cartHasOnlyBillableProducts = state =>
  getCurrentCart(state).products.every(cartProduct => cartProduct.paymentMethod === PAYMENT_BILLABLE);

// Returns whether or not the cart item is expired
export const isShopProductExpired = productInfo => !!productInfo?.expired;

// Returns whether or not the cart item is purchasable
export const isCartProductPurchasable = cartProduct => cartProduct.isPurchasable;

// Returns whether or not the cart item's selected payment method is valid
export const isCartProductPaymentMethodValid = (
  cartProduct,
  productsInfoList,
  selectedDepartment,
  useCurrentContract,
  accountIsBillable,
  unitAllotments
) => {
  const cartProductPaymentMethod = cartProduct.paymentMethod;
  if (cartProductPaymentMethod) {
    const currentProductInfo = findProductInfo(productsInfoList, cartProduct.productCode);

    if (currentProductInfo) {
      return (
        cartProductPaymentMethod &&
        getPaymentMethodsForCartProduct(
          currentProductInfo,
          selectedDepartment,
          useCurrentContract,
          accountIsBillable,
          unitAllotments
        ).some(method => method.value === cartProductPaymentMethod)
      );
    }
  }
  return false;
};

// returns if shop product sizes are found in availability
export const shopProductHasAvailability = (productInfo, productAvailability = {}) => {
  const availability = productAvailability[productInfo.productCode];

  return (
    !!availability &&
    Object.entries(availability).some(([sizeCode, qtys]) => {
      const greaterThanZero = getTotalAvailability(qtys) > 0;
      const existsInProductInfo = productInfo.sizes.some(size => size.sizeCode === sizeCode);

      return greaterThanZero && existsInProductInfo;
    })
  );
};

// Returns if cart item has availability data
export const cartProductHasAvailabilityData = (cartProduct, productAvailability = {}) => {
  const availability = productAvailability[cartProduct.productCode];
  return !!availability;
};

// Returns if cart item has availability
export const cartProductHasAvailability = (cartProduct, productAvailability = {}) => {
  const availability = productAvailability[cartProduct.productCode];
  return !!availability && Object.values(availability).some(size => getTotalAvailability(size) > 0);
};

// Returns whether or not the cart item currently has quantities entered
export const cartProductHasQuantitiesEntered = cartProduct => cartProduct.sizes.some(size => size.quantity > 0);

// Is the given {value} a multiple of {multiple}
export const isValueMultipleOf = (value, multiple) => Number(value) % multiple === 0;

// Returns whether or not cart errors exist
export const hasErrors = (state, errorTypes = ERROR_TYPES_ALL) => {
  const { errors, products } = getCurrentCart(state);
  return (
    errors.some(error => errorIsOfType(error, errorTypes)) ||
    products.some(item => item.errors.some(error => errorIsOfType(error, errorTypes)))
  );
};

// Returns whether or not the current cart has Cart errors
export const hasCartErrors = state => hasErrors(state, [ERROR_TYPE_CART]);

// Returns whether or not the current cart has Checkout errors
export const hasCheckoutErrors = state => hasErrors(state, [ERROR_TYPE_CHECKOUT]);

export const shippingDetailErrors = state =>
  getCurrentCart(state).errors.filter(
    ({ data: { field } }) =>
      field === ERROR_DATA_PHONENUM.field ||
      field === ERROR_DATA_PHONENUM_EXT.field ||
      field === ERROR_DATA_PURCHASE_ORDER_NUM.field ||
      field === ERROR_DATA_SHIPPING_METHOD.field ||
      field === ERROR_DATA_NOTES_TO_NIKE.field
  );

// Returns the current cart's {productGroup} list
export const getCartProductGroups = state => getCurrentCart(state).productGroups;

// Returns a product group given the product group's type
export const getProductGroupByType = (state, productGroupType) =>
  getCartProductGroups(state).find(group => group.type === productGroupType);

// Returns all selected addresses in a single array
export const getSelectedAddresses = state =>
  getCurrentCart(state)
    .productGroups.filter(group => group.cartProductIds.length > 0 && !!group.address)
    .map(group => group.address);

/**
 * @function getCurrentCartEtag
 * @desc get the current carts ETag
 * @param {Object} state
 * @return {String} the current ETag
 */
export const getCurrentCartEtag = state => getCurrentCart(state).etag;

const isValidCartForm = cartForm => typeof cartForm === 'object' && typeof cartForm.values === 'object';

const buildCartForm = (cartForm, productId) => {
  if (!isValidCartForm(cartForm)) return undefined;

  const filteredValueKeys = Object.keys(cartForm.values).filter(key => key.includes(productId));
  const filteredFields = {};
  const filteredValues = {};

  filteredValueKeys.forEach(key => {
    if (cartForm.fields) filteredFields[key] = cartForm.fields[key];
    filteredValues[key] = cartForm.values[key];
  });

  return {
    fields: filteredFields,
    values: filteredValues,
    syncErrors: cartForm.syncErrors
  };
};

export const cartFormHasChanged = (cartForm, nextCartForm, productId) => {
  const form1 = buildCartForm(cartForm, productId);
  const form2 = buildCartForm(nextCartForm, productId);

  return !isEqual(form1, form2);
};
